import { Modal, Tooltip } from '@ui-kit'
import { addToaster } from '@app-store'
import { v4 as uuid } from 'uuid'
import { useEffect, useRef, useState } from 'react'
import { AddDataModal } from '../../components/MultiLookupRowRender'
import { FieldValues } from 'react-hook-form'
import {
  completeTaskByTaskIdApi,
  DataTypeStore,
  DropdownType,
  hideLoader,
  interruptWorkFlow,
  showLoader,
  useDispatch,
  WorkflowInstanceTaskType,
} from '@app-store'
import {
  MISSING_INFO_POPUP_CONSTANTS,
  NAMESPACES,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import {
  DataType,
  OverallMiStatus,
} from '../../types/MissingInformationSummary.types'
import { useAppSelector } from '../../utils/agent.store'
import { useParams } from 'react-router-dom'
import {
  fetchMissingInformationData,
  getAllLovs,
  updateManualMissingFields,
  validateCase,
} from '../../apis/missingInformation.api'
import { MISSING_INFO_CONSTANTS } from '../../constants/missingInfoModal.constants'
import { formatLocalDateWithZone } from '../../utils/date.utils'
import moment from 'moment'
import { MissingInfoPopup } from './MissingInfoPopup'
import { isReadOnlyUser } from '../../utils/permission'
import { startPollingForIntake } from '../../utils/helperFunctions'
import { isEmpty } from 'lodash'

const getCompletableRevalidateTask = (
  tasks: WorkflowInstanceTaskType[] | null | undefined
): WorkflowInstanceTaskType | undefined => {
  const COMPLETABLE_TASK_STATES = new Set([
    WORKFLOW_STATUS.OPEN,
    WORKFLOW_STATUS.PENDING,
  ])

  const list = [...(tasks || [])].reverse()

  const isCompletableTaskState = (state?: string) =>
    !!state && COMPLETABLE_TASK_STATES.has(state)

  const reValidate = list.find(
    (task) =>
      task?.identifier?.ui_identifier === NAMESPACES.RE_VALIDATE &&
      isCompletableTaskState(task?.state)
  )
  if (reValidate) return reValidate
  return list.find(
    (task) => task?.show_on_ui === false && isCompletableTaskState(task?.state)
  )
}

export const MissingInfoIcon = ({
  caseDetails,
  refetchWorkflow,
}: {
  caseDetails: FieldValues
  refetchWorkflow: () => void
}) => {
  const dispatch = useDispatch()
  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const workflowTasks = useAppSelector((state) => state?.workflowData?.taskList)
  const completableRevalidateTask = getCompletableRevalidateTask(workflowTasks)
  const { caseId } = useParams()
  const caseStatusVoidedOrClosed =
    caseDetails?.overallCaseStatus?.toLowerCase() ===
      MISSING_INFO_POPUP_CONSTANTS.CLOSED ||
    caseDetails?.overallCaseStatus?.toLowerCase() === NAMESPACES.VOID
  const hasFetched = useRef(false)
  const [isMiModalOpen, setIsMiModalOpen] = useState<boolean>(false)
  const [totalMissingFields, setTotalMissingFields] = useState<number>(0)
  const [originalMissingData, setOriginaltalMissingData] = useState<DataType[]>(
    []
  )
  const [pendingMiData, setPendingMiData] = useState<DataType[]>([])
  const [resolvedMiData, setResolvedMiData] = useState<DataType[]>([])
  const [overallMiStatus, setOverallMiStatus] =
    useState<OverallMiStatus | null>(null)
  const [miFields, setMiFields] = useState<DropdownType<string>[]>([])
  const toggleMiModal = () => {
    setPendingMiData(originalMissingData)
    setIsMiModalOpen((prev) => !prev)
  }
  const buildPayload = (
    task: WorkflowInstanceTaskType | undefined,
    forced = false
  ) => ({
    taskId: task?.id ?? '',
    meta_data: {
      entityId: task?.identifier?.entity_id,
      layoutId: task?.identifier?.layout_info?.layout_id,
    },
    forced,
  })
  const completeRevalidateTaskWithPolling = async (
    task: WorkflowInstanceTaskType
  ) => {
    const payload = buildPayload(task, true)
    const result = await completeTaskByTaskIdApi(payload)
    if (
      result?.status === 202 ||
      result?.data?.instance?.state === WORKFLOW_STATUS.PROCESSING_IN_BACKGROUND
    ) {
      const poolingResponse: FieldValues = await startPollingForIntake({
        caseId: caseId ?? '',
      })
      result.data = {
        instance: poolingResponse?.workflow_instance,
        preRequisiteExecuted: true,
      }
      if (!isEmpty(poolingResponse?.workflow_instance?.background_response)) {
        result.data.checkerResult =
          poolingResponse?.workflow_instance?.background_response ?? null
      }
    }
    return result
  }
  const updateCaseState = async (pendingData: DataType[]) => {
    try {
      dispatch(showLoader())
      const pendingList = Array.isArray(pendingData) ? pendingData : []
      const hasPending = pendingList.some(
        (item) => item.status === NAMESPACES.PENDING
      )
      if (
        caseDetails &&
        caseDetails?.overallCaseStatus !== NAMESPACES.MISSING_INFORMATION &&
        hasPending
      ) {
        await interruptWorkFlow({
          workflowId: workflowInstance?.id ?? '',
          signal: MISSING_INFO_POPUP_CONSTANTS.MISSING_INFORMATION_INTERRUPT,
        })
        refetchWorkflow()
      } else {
        let saveResult
        if (completableRevalidateTask) {
          try {
            saveResult = await completeRevalidateTaskWithPolling(
              completableRevalidateTask
            )
          } catch (completeError) {
            // CANCELLED→COMPLETED 409 (or other complete failures) must not block MI exit
            console.error(completeError)
          }
        }
        if (
          (saveResult?.data?.checkerResult?.valid &&
            completableRevalidateTask?.identifier?.stopCurrentFlow) ||
          (caseDetails?.overallCaseStatus === NAMESPACES.MISSING_INFORMATION &&
            !hasPending)
        ) {
          await interruptWorkFlow({
            workflowId: workflowInstance?.id ?? '',
            signal: MISSING_INFO_CONSTANTS.STOPCURRENTFLOW,
          })
          refetchWorkflow()
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleSave = async () => {
    try {
      dispatch(showLoader())
      const miDetailsBefore = await fetchMissingInformationData(caseId)
      const wasPendingMI =
        Array.isArray(miDetailsBefore?.data?.pending_missing_info_fields) &&
        miDetailsBefore.data.pending_missing_info_fields.length > 0
      sessionStorage.removeItem(MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA)
      await updateManualMissingFields(pendingMiData)
      await validateCase(caseId)
      const miDetailsAfter = await fetchMissingInformationData(caseId)
      const isPendingMINow =
        Array.isArray(miDetailsAfter?.data?.pending_missing_info_fields) &&
        miDetailsAfter.data.pending_missing_info_fields.length > 0
      if (wasPendingMI && isPendingMINow) {
        dispatch(
          addToaster({
            message: NAMESPACES.MISSING_INFO_TASK_EXISTS,
            type: 'error',
            id: uuid(),
          })
        )
        dispatch(hideLoader())
        toggleMiModal()
        return
      }
      await updateCaseState(miDetailsAfter?.data?.pending_missing_info_fields)
      await getMissingInfo(MISSING_INFO_POPUP_CONSTANTS.SAVE_UPPERCASE)
      toggleMiModal()
    } catch (error) {
      console.error(error)
    } finally {
      getMissingInfo(MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE)
      dispatch(hideLoader())
    }
  }
  const handleReValidate = async () => {
    try {
      dispatch(showLoader())
      const miDetailsBefore = await fetchMissingInformationData(caseId)
      const wasPendingMI =
        Array.isArray(miDetailsBefore?.data?.pending_missing_info_fields) &&
        miDetailsBefore.data.pending_missing_info_fields.length > 0
      saveModalStateInSession(MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE)
      await updateManualMissingFields(pendingMiData)
      await validateCase(caseId)
      const miDetailsAfter = await fetchMissingInformationData(caseId)
      const isPendingMINow =
        Array.isArray(miDetailsAfter?.data?.pending_missing_info_fields) &&
        miDetailsAfter.data.pending_missing_info_fields.length > 0
      await updateCaseState(miDetailsAfter?.data?.pending_missing_info_fields)
      await getMissingInfo(MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE)
      setPendingMiData((prev) =>
        prev.map((item) => ({ ...item, changed: false }))
      )
      if (wasPendingMI && isPendingMINow) {
        dispatch(
          addToaster({
            message: NAMESPACES.MISSING_INFO_TASK_EXISTS,
            type: 'error',
            id: uuid(),
          })
        )
        setPendingMiData((prev) =>
          prev.map((item) => ({ ...item, changed: false }))
        )
        dispatch(hideLoader())
        return
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleCancel = async () => {
    sessionStorage.removeItem(MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA)
    toggleMiModal()
  }
  const sortByMiStartDate = (
    arr: { mi_start_date: string; mi_end_date: string }[]
  ) => {
    if (!Array.isArray(arr)) return []
    return [...arr].sort((a, b) => {
      const format = 'MM/DD/YYYY HH:mm z'
      const dateA = a.mi_start_date ? moment(a.mi_start_date, format) : null
      const dateB = b.mi_start_date ? moment(b.mi_start_date, format) : null
      if (!dateA && !dateB) return 0
      if (!dateA) return -1
      if (!dateB) return 1
      if (dateA.isBefore(dateB)) return 1
      if (dateA.isAfter(dateB)) return -1
      return 0
    })
  }
  const getMissingInfo = async (type: string) => {
    try {
      const miDetails = await fetchMissingInformationData(caseId)
      // resolved fields
      setResolvedMiData(
        sortByMiStartDate(
          miDetails.data?.resolved_missing_info_fields?.map(
            (item: DataType) => ({
              ...item,
              mi_start_date: formatLocalDateWithZone(
                item?.mi_start_date as string
              ),
              mi_end_date: formatLocalDateWithZone(item?.mi_end_date as string),
              mi_field_type: item?.manually_inserted
                ? MISSING_INFO_POPUP_CONSTANTS.MANUAL
                : MISSING_INFO_POPUP_CONSTANTS.SYSTEM,
            })
          )
        ) as DataTypeStore[]
      )
      // overall MI status
      setOverallMiStatus({
        mi_start_date: miDetails?.data?.mi_start_date,
        mi_end_date: miDetails?.data?.mi_end_date,
        mi_duration: miDetails?.data?.mi_duration,
      })
      const finalArray = miDetails?.data?.pending_missing_info_fields?.map(
        (item: DataType) => ({
          ...item,
          mi_start_date: formatLocalDateWithZone(item?.mi_start_date as string),
          mi_end_date: formatLocalDateWithZone(item?.mi_end_date as string),
          mi_field_type: item?.manually_inserted
            ? MISSING_INFO_POPUP_CONSTANTS.MANUAL
            : MISSING_INFO_POPUP_CONSTANTS.SYSTEM,
        })
      )
      const sortedArray = sortByMiStartDate(finalArray)
      setOriginaltalMissingData(sortedArray)
      setTotalMissingFields(finalArray.length)
      if (
        type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK ||
        type === MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE ||
        type === MISSING_INFO_POPUP_CONSTANTS.SAVE_UPPERCASE ||
        !hasFetched.current
      ) {
        // Clear 'changed' flag after revalidation
        const dataToSet =
          type === MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE
            ? sortedArray.map((item) => ({ ...item, changed: false }))
            : sortedArray
        setPendingMiData(dataToSet)
      }
    } catch (err) {
      console.error(err)
    }
  }
  const saveModalStateInSession = (type: string) => {
    const stored = sessionStorage.getItem(
      MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA
    )
    let parsed
    if (type === MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE) {
      parsed = {
        openModal: true,
        caseId: caseId,
        manual_mi_fields: [],
      }
    } else if (stored) {
      parsed = JSON.parse(stored)
      if (!parsed.openModal) {
        parsed = { ...parsed, manual_mi_fields: [] }
      }
    }
    if (parsed && parsed.caseId === caseId?.toString()) {
      sessionStorage.setItem(
        MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA,
        JSON.stringify(parsed)
      )
    }
  }
  const checkMissingInfoWithNoFields = async () => {
    if (
      caseDetails?.overallCaseStatus === NAMESPACES.MISSING_INFORMATION &&
      totalMissingFields === 0
    ) {
      await interruptWorkFlow({
        workflowId: workflowInstance?.id ?? '',
        signal: MISSING_INFO_CONSTANTS.STOPCURRENTFLOW,
      })
      refetchWorkflow()
    }
  }
  const fetchMissingInfoDetails = async ({ type = '' }: { type?: string }) => {
    dispatch(showLoader())
    try {
      saveModalStateInSession(type)
      if (type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK) {
        await checkMissingInfoWithNoFields()
      }
      if (
        !isReadOnlyUser() &&
        !caseStatusVoidedOrClosed &&
        caseDetails &&
        caseDetails?.overallCaseStatus === NAMESPACES.MISSING_INFORMATION &&
        completableRevalidateTask &&
        type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK
      ) {
        try {
          await validateCase(caseId)
          const defaultResult = await completeRevalidateTaskWithPolling(
            completableRevalidateTask
          )
          if (
            defaultResult?.data?.checkerResult?.valid &&
            completableRevalidateTask?.identifier?.stopCurrentFlow
          ) {
            await interruptWorkFlow({
              workflowId: workflowInstance?.id ?? '',
              signal: MISSING_INFO_CONSTANTS.STOPCURRENTFLOW,
            })
            refetchWorkflow()
          }
        } catch (completeError) {
          console.error(completeError)
        }
      }
      if (
        type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK ||
        !hasFetched.current
      ) {
        await getMissingInfo(type)
      }
    } catch (err) {
      console.error(err)
    } finally {
      if (!hasFetched.current) {
        modalBasedOnSessionStorage()
      }
      dispatch(hideLoader())
    }
  }
  const modalBasedOnSessionStorage = () => {
    const stored = sessionStorage.getItem(
      MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA
    )
    if (stored) {
      let parsed = JSON.parse(stored)
      if (parsed.caseId === caseId?.toString()) {
        if (parsed.openModal) {
          setIsMiModalOpen(true)
          if (parsed?.manual_mi_fields.length > 0) {
            setPendingMiData(parsed.manual_mi_fields)
          }
          parsed = { ...parsed, openModal: false }
          sessionStorage.setItem(
            MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA,
            JSON.stringify(parsed)
          )
          fetchLovs()
          hasFetched.current = true
        }
      } else {
        sessionStorage.removeItem(MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA)
      }
    }
  }
  const handleIconClick = async () => {
    if (workflowDefinition?.global_variables?.new_flow && caseDetails.caseId) {
      await fetchMissingInfoDetails({
        type: MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK,
      })
    }
    setIsMiModalOpen(true)
    fetchLovs()
  }
  const fetchLovs = async () => {
    const result = await getAllLovs()
    setMiFields(
      result?.data?.map(
        (item: {
          mi_field: string
          mi_code: string
          template_label: string
        }) => ({
          ...item,
          label: item.mi_field,
          value: item.mi_field,
        })
      )
    )
  }
  useEffect(() => {
    if (
      workflowDefinition?.global_variables?.new_flow &&
      !!caseDetails.caseId
    ) {
      fetchMissingInfoDetails({
        type: MISSING_INFO_POPUP_CONSTANTS.INITIAL_LOAD,
      })
    }
  }, [caseDetails])
  if (workflowDefinition?.global_variables?.new_flow && caseDetails.caseId) {
    return (
      <>
        <span className="flex ml-[0.625rem] items-center gap-[0.6875rem]">
          <Tooltip
            content={MISSING_INFO_POPUP_CONSTANTS.MISSING_INFORMATION_SUMMARY}
          >
            <button
              className={`flex items-center justify-center w-[1.125rem] h-[1.125rem] text-white rounded-full ${
                totalMissingFields > 0 ? 'bg-[#C8355A]' : 'bg-green-600'
              } font-bold text-xs`}
              type="button"
              onClick={handleIconClick}
            >
              {totalMissingFields}
            </button>
          </Tooltip>
        </span>
        {isMiModalOpen && (
          <AddDataModal>
            <Modal
              isOpen={isMiModalOpen}
              closeModal={toggleMiModal}
              showCloseButton={false}
              width="min-w-[60vw] max-w-[40%]"
              isDragable={true}
              isBackdropInteractive={true}
              isResizable={true}
              minResizeWidth={520}
              minResizeHeight={180}
            >
              <MissingInfoPopup
                handleCancel={handleCancel}
                setPendingMiData={setPendingMiData}
                pendingMiData={pendingMiData}
                overallMiStatus={overallMiStatus}
                resolvedMiData={resolvedMiData}
                handleSave={handleSave}
                handleReValidate={handleReValidate}
                originalMissingData={originalMissingData}
                miFields={miFields}
                caseStatusVoidedOrClosed={caseStatusVoidedOrClosed}
              />
            </Modal>
          </AddDataModal>
        )}
      </>
    )
  }
}
